import { STATUS_TONE_DOT, STATUS_TONE_FILL, type StatusTone } from '@/lib/ui/status-tone';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

type StatusBadgeProps = {
  tone: StatusTone;
  children: ReactNode;
  /** Visually smaller variant used inside dense lists. */
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * Small pill used for status (active / at-risk / profit / loss / etc.).
 * Always route semantic status through this component and a `StatusTone` —
 * never introduce raw green/red/amber/blue Tailwind classes.
 */
export function StatusBadge({ tone, children, size = 'md', className }: StatusBadgeProps) {
  const sizeClass = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-sm font-medium',
        sizeClass,
        STATUS_TONE_FILL[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/** Small filled dot (used beside a label instead of a pill). */
export function StatusDot({
  tone,
  className,
  pulse = false,
  label,
}: {
  tone: StatusTone;
  className?: string;
  pulse?: boolean;
  /** Accessible label — pass `null` to opt into `aria-hidden`. */
  label: string | null;
}) {
  return (
    <span
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        STATUS_TONE_DOT[tone],
        pulse && 'animate-pulse',
        className
      )}
      role={label === null ? undefined : 'status'}
      aria-label={label ?? undefined}
      aria-hidden={label === null ? true : undefined}
    />
  );
}
